"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { forceUnlockScroll } from "@/lib/scroll-lock";

/**
 * Monitors route transitions and browser back/forward (popstate) actions.
 * Guarantees that body and html scrolling is never stuck or frozen
 * when navigating between pages or navigating back.
 */
export function NavigationWatcher() {
  const pathname = usePathname();

  useEffect(() => {
    // Force-unlock scroll whenever pathname changes
    forceUnlockScroll();

    // Secondary cleanup after Next.js completes DOM reconciliation
    const t1 = window.setTimeout(forceUnlockScroll, 60);
    const t2 = window.setTimeout(forceUnlockScroll, 250);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [pathname]);

  useEffect(() => {
    const handlePopState = () => {
      // Browser back/forward button clicked or history restored: instantly unlock scroll
      forceUnlockScroll();
      window.setTimeout(forceUnlockScroll, 60);
      window.setTimeout(forceUnlockScroll, 250);
    };

    const handlePageShow = () => {
      // BFCache restoration (e.g. returning via back button)
      forceUnlockScroll();
      window.setTimeout(forceUnlockScroll, 60);
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("hashchange", handlePopState);

    // Auto-recover from stale ChunkLoadErrors when a new production deployment occurs
    const handleChunkError = (e: Event) => {
      let msg = "";
      if ("message" in e && typeof (e as ErrorEvent).message === "string") {
        msg = (e as ErrorEvent).message;
      } else if ("reason" in e) {
        const reason = (e as PromiseRejectionEvent).reason;
        msg = typeof reason?.message === "string" ? reason.message : String(reason || "");
      }

      const isChunk =
        /Loading chunk/i.test(msg) ||
        /Failed to load chunk/i.test(msg) ||
        /ChunkLoadError/i.test(msg);

      if (isChunk) {
        const last = sessionStorage.getItem("targetqa_chunk_reload_ts");
        const now = Date.now();
        if (!last || now - parseInt(last, 10) > 15000) {
          sessionStorage.setItem("targetqa_chunk_reload_ts", now.toString());
          window.location.reload();
        }
      }
    };

    window.addEventListener("error", handleChunkError);
    window.addEventListener("unhandledrejection", handleChunkError);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("hashchange", handlePopState);
      window.removeEventListener("error", handleChunkError);
      window.removeEventListener("unhandledrejection", handleChunkError);
    };
  }, []);

  return null;
}
